import React, { useEffect, useState, useRef, useContext } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import ConsolidationResult from './ConsolidationResult';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InteractiveSketch from './InteractiveSketch';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';

const Consolidation = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [z1, setz1] = useState('');
  const [z2, setz2] = useState('');
  const [df, setdf] = useState('');
  const [b, setb] = useState('');
  const [l, setl] = useState('');
  const [q, setq] = useState('');
  const [clayType, setclayType] = useState(0);
  const [drainageType, setdrainageType] = useState(0);
  const [mv, setmv] = useState('');
  const [cv, setcv] = useState('');
  const [t, sett] = useState('');
  //PDF RELATED STATES
  const sketchContainerRef = useRef(null);
  const uvtContainerRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);
  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setz1(inputs.z1);
    setz2(inputs.z2);
    setdf(inputs.df);
    setb(inputs.b);
    setl(inputs.l);
    setq(inputs.q);
    setclayType(inputs.clayType);
    setdrainageType(inputs.drainageType);
    setmv(inputs.mv);
    setcv(inputs.cv);
    sett(inputs.t);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    z1,
    z2,
    df,
    b,
    l,
    q,
    clayType,
    drainageType,
    mv,
    cv,
    t,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);
    setSaveButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.z1 || calculationInput.z1 === 0) &&
      (calculationInput.z2 || calculationInput.z2 === 0) &&
      (calculationInput.df || calculationInput.df === 0) &&
      (calculationInput.b || calculationInput.b === 0) &&
      (calculationInput.l || calculationInput.l === 0) &&
      (calculationInput.q || calculationInput.q === 0) &&
      calculationInput.clayType &&
      calculationInput.drainageType &&
      (calculationInput.mv || calculationInput.mv === 0) &&
      (calculationInput.cv || calculationInput.cv === 0) &&
      (calculationInput.t || calculationInput.t === 0)
    ) {
      setCalculateButtonDisabled(false);
      setSaveButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(true);
      setSaveButtonDisabled(true);
    }
  }, [
    setSaveButtonDisabled,
    setCalculateButtonDisabled,
    calculationInput.name,
    calculationInput.z1,
    calculationInput.z2,
    calculationInput.df,
    calculationInput.b,
    calculationInput.l,
    calculationInput.q,
    calculationInput.clayType,
    calculationInput.drainageType,
    calculationInput.mv,
    calculationInput.cv,
    calculationInput.t,
  ]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    calculationInput.z1,
    calculationInput.z2,
    calculationInput.df,
    calculationInput.b,
    calculationInput.l,
    calculationInput.q,
    calculationInput.clayType,
    calculationInput.drainageType,
    calculationInput.mv,
    calculationInput.cv,
    calculationInput.t,
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setName('');
    setz1('');
    setz2('');
    setdf('');
    setb('');
    setl('');
    setq('');
    setclayType(0);
    setdrainageType(0);
    setmv('');
    setcv('');
    sett('');
    setShowOutput(false);
  };

  const URL = {
    CALCULATE: '/api/v1/calculations/consolidation',
    LOADONE: `/api/v1/calculations/consolidation/loadOne/`,
    LOADALL: '/api/v1/calculations/consolidation/loadAll',
    UPDATE: `/api/v1/calculations/consolidation/update/`,
    SAVE: '/api/v1/calculations/consolidation/save',
    CHECKUNIQUENAME: `/api/v1/calculations/consolidation/checkUniqueName/`,
    DELETE: '/api/v1/calculations/consolidation/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  // HANDLE INPUT CHANGES and WARNINGS
  // -- INPUT TYPE WARNINGS  -- ONCHANGE

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  //  -- LOGIC WARNINGS -- ONBLUR
  const handleLogicZ1 = (e) => {
    if (e.target.value && z2) {
      if (parseFloat(e.target.value) > parseFloat(z2)) {
        showMuiSnack('z2 must be greater than z1', 'warning');
      }
    }
  };
  const handleLogicZ2 = (e) => {
    if (z1 && e.target.value) {
      if (parseFloat(z1) > parseFloat(e.target.value)) {
        showMuiSnack('z2 must be greater than z1', 'warning');
      }
    }
  };
  const handleLogicB = (e) => {
    if (e.target.value && l) {
      if (parseFloat(e.target.value) > parseFloat(l)) {
        showMuiSnack('L must be greater than B', 'warning');
      }
    }
  };
  const handleLogicL = (e) => {
    if (b && e.target.value) {
      if (parseFloat(e.target.value) < parseFloat(b)) {
        showMuiSnack('L must be greater than B', 'warning');
      }
    }
  };

  //TABS
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  //HANDLE PDF
  const handleExportPdf = async () => {
    if (isExporting) return;
    if (user.role === 'basic' || user.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const [sketchPngBase64, uvtPngBase64] = await Promise.all([
        svgContainerToPngBase64(sketchContainerRef.current, {
          scale: 2, // 1)Low resolution, 2)High Resolution
        }),
        svgContainerToPngBase64(uvtContainerRef.current, {
          scale: 2, // 1)Low resolution, 2)High Resolution
        }),
      ]);

      const payload = {
        ...calculationInput,
        sketchPngBase64,
        uvtPngBase64,
      };

      const res = await fetch('/api/v1/pdf/consolidation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('PDF generation failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-consolidation-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      // optionally show a snackbar
    } finally {
      setIsExporting(false);
    }
  };
  // RETURNING JSX
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Consolidation Settlement Calculator"
        description="Compute time-dependent consolidation settlement of shallow foundations using mv, cv, drainage condition (one-way/two-way), load geometry, and time."
        url="https://www.geocalc.app/calculations/consolidation"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg" // optional; remove if not available
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Consolidation Settlement Calculator', url: 'https://www.geocalc.app/calculations/consolidation' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} autoHideDuration={3000} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}

        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user.role} />
        <div className="right-calculation">
          <div className="consolidationContainer">
            <h1>Time Dependent Settlements of Shallow Foundations</h1>
            <div className="pilerocksettleTabContainer">
              <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                <Tab label="Clay Layer Geometry" sx={{ textTransform: 'none' }} />
                <Tab label="Foundation Geometry & Load" sx={{ textTransform: 'none' }} />
                <Tab label="Consolidation Parameters" sx={{ textTransform: 'none' }} />
              </Tabs>
            </div>
            {/* Clay layer geometry inputs */}
            {value === 0 && (
              <div className="consolidationInputs">
                <TextField
                  style={{ maxWidth: 160 }}
                  size="small"
                  label="Name"
                  variant="outlined"
                  inputProps={{
                    maxLength: `${MAX_NAME_LENGTH}`,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={name}
                  onChange={handleChangeName}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label={
                    <span key={'z1'}>
                      z<sub>1</sub> (m)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Starting point of compressible layer measured from ground surface"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={z1}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'z1', setz1)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                  onBlur={handleLogicZ1}
                />

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label={
                    <span key={'z2'}>
                      z<sub>2</sub> (m)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Ending point of compressible layer measured from ground surface"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={z2}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'z2', setz2)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                  onBlur={handleLogicZ2}
                />
              </div>
            )}
            {/* Foundation geometry inputs */}
            {value === 1 && (
              <div className="consolidationInputs">
                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label={
                    <span key={'df'}>
                      d<sub>f</sub> (m)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation depth" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={df}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Df', setdf)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label="B (m)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation width" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={b}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'B', setb)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                  onBlur={handleLogicB}
                />

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label="L (m)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Foundation length" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={l}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'L', setl)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                  onBlur={handleLogicL}
                />
                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label="q (kPa)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Stress due to foundation loads" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={q}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'q', setq)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {/* Consolidation parameters inputs */}
            {value === 2 && (
              <div className="consolidationInputs">
                <TextField
                  size="small"
                  style={{ minWidth: 150, maxWidth: 160 }}
                  select
                  label="Clay type"
                  value={clayType}
                  onChange={(e) => setclayType(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem disabled value="0">
                    Clay Type
                  </MenuItem>
                  <MenuItem value="1">Sensitive</MenuItem>
                  <MenuItem value="2">Normally Consolidated</MenuItem>
                  <MenuItem value="3">Overconsolidated</MenuItem>
                  <MenuItem value="4">Heavily Overconsolidated</MenuItem>
                </TextField>

                <TextField
                  size="small"
                  style={{ minWidth: 150, maxWidth: 160 }}
                  select
                  label="Drainage Type"
                  value={drainageType}
                  onChange={(e) => setdrainageType(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem disabled value="0">
                    Drainage Type
                  </MenuItem>
                  <MenuItem value="1">One Way</MenuItem>
                  <MenuItem value="2">Two Way</MenuItem>
                </TextField>

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label={
                    <span key={'mv'}>
                      m<sub>v</sub> (m<sup>2</sup>/kNx10 <sup>-5</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Coefficient of compressibility" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={mv}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'mv', setmv)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label={
                    <span key={'cv'}>
                      c<sub>v</sub> (m<sup>2</sup>/sx10 <sup>-8</sup>)
                    </span>
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Coefficient of consolidation" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={cv}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'cv', setcv)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />

                <TextField
                  size="small"
                  style={{ maxWidth: 160 }}
                  label="t (month)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Consolidation time. Max 360 months" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={t}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 't', sett)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            <InteractiveSketch
              name={name}
              z1={z1}
              z2={z2}
              df={df}
              b={b}
              l={l}
              q={q}
              clayType={clayType}
              drainageType={drainageType}
              mv={mv}
              cv={cv}
              t={t}
            />
            {/* For PDF Exports */}
            <div
              ref={sketchContainerRef}
              style={{
                position: 'absolute',
                left: -99999,
                top: -99999,
                width: 800,
                height: 350,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <InteractiveSketch
                name={name}
                z1={z1}
                z2={z2}
                df={df}
                b={b}
                l={l}
                q={q}
                clayType={clayType}
                drainageType={drainageType}
                mv={mv}
                cv={cv}
                t={t}
                variant={'pdf-light'}
              />
            </div>
            <div className="buttonContainer pilerocksettleSaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF…
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>

              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
            {isLoadingLoad && <CircularProgress size={30} />}
            {isLoadingCalculate && (
              <div className="centerLoading">
                <CircularProgress />
              </div>
            )}
          </div>
          {showOutput && <ConsolidationResult result={result} uvtContainerRef={uvtContainerRef} />}
        </div>
      </div>
      <Footer />
    </>
  );
};

const ConsolidationWithProvider = () => {
  return (
    <CalculationContextProvider>
      <Consolidation />
    </CalculationContextProvider>
  );
};

export default ConsolidationWithProvider;
